import { Controller } from '@hotwired/stimulus'

interface Chip {
  value?: string
  label?: string
}

export default class extends Controller {
  static targets = [
    'chips',
    'chipTemplate',
    'resetButton',
    'checkbox',
    'select',
    'radio',
    'search',
    'extraChips',
    'showAll'
  ]

  private expanded: boolean = false
  declare readonly chipsTarget: HTMLElement
  declare readonly chipTemplateTarget: HTMLTemplateElement
  declare readonly resetButtonTarget: HTMLButtonElement
  declare readonly checkboxTargets: HTMLInputElement[]
  declare readonly radioTargets: HTMLInputElement[]
  declare readonly selectTargets: HTMLSelectElement[]
  declare readonly searchTarget: HTMLInputElement
  declare readonly extraChipsTarget: HTMLButtonElement
  declare readonly showAllTarget: HTMLButtonElement

  connect (): void {
    this.initializeFilters()
    this.updateVisibility()
  }

  radio (event: Event): void {
    const radio = event.target as HTMLInputElement
    const radios = this.radioTargets.filter((cb) => cb.name === radio.name)

    for (const r of radios) {
      const label = r?.dataset?.label ?? ''

      if (radio.value === r.value && r.checked) {
        if (this.findChip(label, radio.value, 'radio') == null) {
          this.createChip(label, radio.value, 'radio')
        }
      } else {
        this.deleteChip(label, r.value, 'radio', event)
      }
    }

    this.submitForm(event)
  }

  check (event: Event): void {
    const checkbox = event.target as HTMLInputElement
    const label = checkbox?.dataset?.label ?? ''

    if (checkbox.checked) {
      this.createChip(label, checkbox.value, 'checkbox')
    } else {
      this.deleteChip(label, checkbox.value, 'checkbox', event)
    }

    this.submitForm(event)
  }

  select (event: Event): void {
    const select = event.target as HTMLSelectElement

    for (const option of select.options) {
      if (option.selected) {
        if (this.findChip(option.text, option.value, 'select') == null) {
          this.createChip(option.text, option.value, 'select')
        }
      } else {
        this.deleteChip(option.text, option.value, 'select', event)
      }
    }

    this.submitForm(event)
  }

  showAll (): void {
    this.expanded = !this.expanded
    this.updateVisibility()
  }

  remove (event: Event): void {
    const target = event.target as HTMLElement
    const chip = target.closest('[data-value][data-label]') as HTMLElement

    if (chip != null) {
      const { value, label } = chip.dataset as Chip

      if (value === undefined || label === undefined) {
        return
      }

      chip.remove()

      switch (chip.dataset.type) {
        case 'checkbox':
          this.unselectCheckbox(value, label, event)
          break
        case 'radio':
          this.uncheckRadio(value, label, event)
          break
        case 'select':
          this.uncheckSelect(value, label, event)
          break
      }
    }
  }

  submitForm (event: Event): void {
    const form = this.element as HTMLFormElement

    event.preventDefault()
    form.requestSubmit()

    this.updateVisibility()
  }

  resetSearch (event: Event): void {
    this.searchTarget.value = ''
    this.submitForm(event)
  }

  resetFilters (event: Event): void {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false
      this.deleteChip(checkbox.dataset.label ?? '', checkbox.value, 'checkbox', event)
    })

    this.radioTargets.forEach((radio) => {
      const chip = this.findChip(radio.dataset.label ?? '', radio.value, 'radio')

      if (chip != null) {
        const firstRadio = this.radioTargets.find((cb) => cb.name === radio.name)

        if (firstRadio != null) {
          firstRadio.checked = true
          this.createChip(firstRadio.dataset.label ?? '', firstRadio.value, 'radio')
        }

        this.deleteChip(radio.dataset.label ?? '', radio.value, 'radio', event)
      }
    })

    this.selectTargets.forEach((select: HTMLSelectElement) => {
      for (const option of select.options) {
        if (option.selected) {
          this.deleteChip(option.text, option.value, 'select', event)
        }
      }

      if ((select as any).tomselect !== undefined) {
        (select as any).tomselect.clear()
      }
    })

    this.submitForm(event)
  }

  private initializeFilters (): void {
    this.checkboxTargets.forEach(cb => {
      if (cb.checked) {
        this.createChip(cb.dataset.label ?? '', cb.value, 'checkbox')
      }
    })

    this.radioTargets.forEach(radio => {
      if (radio.checked) {
        this.createChip(radio.dataset.label ?? '', radio.value, 'radio')
      }
    })

    this.selectTargets.forEach((sb: HTMLSelectElement) => {
      for (const option of sb.options) {
        if (option.selected) {
          this.createChip(option.text, option.value, 'select')
        }
      }
    })
  }

  private createChip (label: string, value: string, type: string): void {
    const chipClone = this.chipTemplateTarget.content.cloneNode(true) as DocumentFragment
    const chipElement = chipClone.querySelector('div')
    const chipLabel = chipClone.querySelector('span')

    if (chipElement != null && chipLabel != null) {
      chipElement.dataset.value = value
      chipElement.dataset.label = label
      chipElement.dataset.type = type
      chipLabel.textContent = label

      if (value === 'all') {
        chipElement.querySelector('svg')?.remove()
      }

      const visibleChipCount = this.visibleChipCount()
      if (visibleChipCount >= 3) {
        chipElement.classList.add('hidden')
      }

      this.chipsTarget.appendChild(chipElement)
      this.updateVisibility()
    }
  }

  private visibleChipCount (): number {
    return Array.from(this.chipsTarget.children)
      .filter(el => el instanceof HTMLElement && el.hasAttribute('data-value') && !el.classList.contains('hidden'))
      .length
  }

  private deleteChip (label: string, value: string, type: string, event: Event): void {
    const chip = this.findChip(label, value, type)

    if (chip != null) {
      chip.remove()
    }

    this.submitForm(event)
  }

  private findChip (label: string, value: string, type: string): HTMLElement | null {
    return Array.from(this.chipsTarget.children).find(
      (chip): chip is HTMLElement =>
        chip instanceof HTMLElement &&
        chip.dataset.value === value &&
        chip.dataset.label === label
    ) ?? null
  }

  private unselectCheckbox (value: string, label: string, event: Event): void {
    const checkbox = this.checkboxTargets.find(
      (cb) => cb.value === value && cb.dataset.label === label
    )

    if (checkbox != null) {
      checkbox.checked = false
      this.submitForm(event)
    }
  }

  private uncheckRadio (value: string, label: string, event: Event): void {
    const radio = this.radioTargets.find(
      (radio) => radio.value === value && radio.dataset.label === label
    )

    if (radio == null) {
      return
    }

    const firstRadio = this.radioTargets.find((r) => r.name === radio.name)
    if (firstRadio != null) {
      firstRadio.checked = true
      this.createChip(firstRadio.dataset.label ?? '', firstRadio.value, 'radio')
      this.submitForm(event)
    }
  }

  private uncheckSelect (value: string, label: string, event: Event): void {
    this.selectTargets.forEach((select) => {
      for (const option of select.options) {
        if (value === option.value && label === option.text) {
          const select = option.parentElement as any
          option.selected = false

          if (select.tomselect !== undefined) {
            select.tomselect.removeItem(option.value)
          }

          this.submitForm(event)
        }
      }
    })
  }

  private updateVisibility (): void {
    setTimeout(() => {
      const chips = Array.from(this.chipsTarget.querySelectorAll<HTMLElement>('[data-value]'))
      const filteredChips = chips.filter(chip => chip.dataset.value !== 'all')
      const showChips = filteredChips.length > 0
      const onlyDefaultFilter = filteredChips.length === 1 && filteredChips[0].dataset.value === 'all'

      if (this.expanded) {
        filteredChips.forEach(chip => {
          chip.classList.remove('hidden')
          chip.style.display = 'flex'
        })
        this.extraChipsTarget.style.display = 'none'
        this.showAllTarget.textContent = 'Show less'
      } else {
        filteredChips.forEach((chip, index) => {
          if (index < 3) {
            chip.classList.remove('hidden')
            chip.style.display = 'flex'
          } else {
            chip.classList.add('hidden')
            chip.style.display = 'none'
          }
        })
        const hiddenCount = filteredChips.length > 3 ? filteredChips.length - 3 : 0
        this.extraChipsTarget.textContent = hiddenCount > 0 ? `+${hiddenCount} more` : ''
        this.extraChipsTarget.style.display = hiddenCount > 0 ? 'inline-block' : 'none'
        this.showAllTarget.textContent = hiddenCount > 0 ? 'Show all' : ''
      }

      this.showAllTarget.style.display = filteredChips.length > 3 ? 'inline-block' : 'none'

      this.resetButtonTarget.style.display = showChips && !onlyDefaultFilter ? 'block' : 'none'

      if (this.chipsTarget.parentElement != null) {
        this.chipsTarget.parentElement.style.display = showChips ? 'flex' : 'none'
      }
    }, 0)
  }
}
